/** VMT recovery sentence from annual LDV VMT series (same units as chart). */
export function buildVmtRecoverySentence(
  years: number[],
  annualLdvVmt: number[],
  baselineYear = 2019
): string {
  if (!years.length || years.length !== annualLdvVmt.length) {
    return 'Recent values reflect modeled and backcast estimates in the displayed series.';
  }
  const byYear = new Map<number, number>();
  years.forEach((y, i) => {
    const v = annualLdvVmt[i];
    if (Number.isFinite(v) && v > 0) byYear.set(y, v);
  });
  const baseline = byYear.get(baselineYear);
  const reportingYear = Math.max(...years.filter((y) => (byYear.get(y) ?? 0) > 0));
  const latest = byYear.get(reportingYear);
  const idx2020 = byYear.get(2020);
  if (baseline == null || latest == null) {
    return 'The displayed series combines Replica estimates for recent years with backcast values for earlier years.';
  }
  const pctFrom2019 = ((latest - baseline) / baseline) * 100;
  const droppedIn2020 =
    idx2020 != null && idx2020 < baseline ? ((baseline - idx2020) / baseline) * 100 : null;
  if (latest < baseline * 0.98) {
    const drop2020 =
      droppedIn2020 != null
        ? `Travel fell about ${Math.abs(droppedIn2020).toFixed(0)}% from ${baselineYear} to 2020`
        : `Travel fell from ${baselineYear} to 2020`;
    return `${drop2020}; in ${reportingYear} it remained about ${Math.abs(pctFrom2019).toFixed(0)}% below the ${baselineYear} level in this series.`;
  }
  if (latest > baseline * 1.02) {
    const lead =
      droppedIn2020 != null
        ? `After a drop of about ${Math.abs(droppedIn2020).toFixed(0)}% in 2020, `
        : 'After the 2020 dip, ';
    return `${lead}${reportingYear} travel was about ${pctFrom2019.toFixed(0)}% above the ${baselineYear} level in this series.`;
  }
  const drop2020 =
    droppedIn2020 != null
      ? `After a drop of about ${Math.abs(droppedIn2020).toFixed(0)}% in 2020, `
      : 'After the 2020 dip, ';
  return `${drop2020}${reportingYear} travel was near the ${baselineYear} level in this series.`;
}

export function formatPctChangeSince(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return 'an unavailable change';
  const sign = value > 0 ? 'an increase' : value < 0 ? 'a decrease' : 'no change';
  return `${sign} of ${Math.abs(value).toFixed(1)}%`;
}

export function formatVsCalifornia(local: number, state: number): string {
  if (!Number.isFinite(local) || !Number.isFinite(state)) return 'compared with California';
  if (local > state) return `above`;
  if (local < state) return `below`;
  return `about the same as`;
}
